// web/src/components/AchievementToast.tsx
// Stack of slide-in toasts shown when a match POST returns newAchievements[].
// Each toast auto-dismisses after 6s or on click; rarity tints the border.
//
// Behaviour:
//   - Receives a queue of unlocks; mounts one toast per item, stacked.
//   - Each toast auto-fades after AUTO_DISMISS_MS unless the user clicks ✕.
//   - We don't persist seen-state — the caller controls when to show.

import { useEffect, useState } from 'react';

export interface NewAchievement {
  id: string;
  title: string;
  description: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface Props {
  achievements: NewAchievement[];
}

const RARITY_BORDER: Record<NewAchievement['rarity'], string> = {
  common: '#7F8E81',
  rare: '#3F73B9',
  epic: '#7A4BB9',
  legendary: '#C68B47',
};

const AUTO_DISMISS_MS = 6000;

export function AchievementToastStack({ achievements }: Props) {
  const [visible, setVisible] = useState<NewAchievement[]>(achievements);

  // If the caller re-renders with a new list, reset the stack.
  useEffect(() => { setVisible(achievements); }, [achievements]);

  if (visible.length === 0) return null;

  const dismiss = (id: string) => {
    setVisible((prev) => prev.filter((a) => a.id !== id));
  };

  return (
    <div
      style={{
        position: 'fixed',
        // Clear the sticky header (sits at top:0, z:10, ~64px tall with
        // 14px vertical padding + 36px logo). Without this the toast's ✕
        // landed right on top of "Sign out" in the nav — fixed 2026-05-20.
        top: 80,
        right: 16,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 360,
        pointerEvents: 'none',  // children re-enable
      }}
      role="status"
      aria-live="polite"
    >
      {visible.map((a, i) => (
        <ToastCard
          key={a.id}
          achievement={a}
          onDismiss={() => dismiss(a.id)}
          // Stagger the entry + auto-dismiss so a hat-trick that unlocks 4
          // badges doesn't all crash in at once. Each toast lives 6s from
          // its own entry, so the last one still has plenty of read time.
          entryDelay={i * 180}
        />
      ))}
    </div>
  );
}

function ToastCard({
  achievement,
  onDismiss,
  entryDelay = 0,
}: {
  achievement: NewAchievement;
  onDismiss: () => void;
  entryDelay?: number;
}) {
  useEffect(() => {
    const t = window.setTimeout(onDismiss, AUTO_DISMISS_MS + entryDelay);
    return () => window.clearTimeout(t);
  }, [onDismiss, entryDelay]);

  return (
    <div
      style={{
        pointerEvents: 'auto',
        padding: '12px 16px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${RARITY_BORDER[achievement.rarity]}`,
        borderRadius: 12,
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        // Stagger the visual entry to match the dismiss-timer stagger above
        // — the `both` keeps the keyframes' `from` state held until the delay
        // elapses, so toasts don't all flash on-screen then slide in.
        animation: `mfj-toast-in 220ms ease-out ${entryDelay}ms both`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            color: RARITY_BORDER[achievement.rarity],
            fontWeight: 600,
            marginBottom: 2,
          }}>
            🏆 Unlocked · {achievement.rarity}
          </div>
          <div style={{ fontSize: 15, fontWeight: 500, color: 'var(--ink)', marginBottom: 2 }}>
            {achievement.title}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.4 }}>
            {achievement.description}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--muted)',
            cursor: 'pointer',
            fontSize: 16,
            padding: 0,
            lineHeight: 1,
          }}
        >
          ✕
        </button>
      </div>
      <style>{`
        @keyframes mfj-toast-in {
          from { transform: translateX(20px); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
